import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import './Blog.css';

const blogPosts = [
  {
    id: 1,
    slug: 'top-personalised-gift-ideas-2025',
    title: 'Top 10 Personalised Gift Ideas That Will Wow Them in 2025',
    category: 'Gift Ideas',
    author: 'Feni Shah',
    date: 'April 12, 2025',
    readTime: '5 min read',
    excerpt: 'Discover the most thoughtful personalised gifts that go beyond the ordinary — from engraved leather sets to custom star maps and memory hampers.',
    cover: 'https://images.unsplash.com/photo-1549007994-cb92caebd54b?w=800&q=80',
    body: `Gift-giving has evolved beautifully. Today, the most cherished presents aren't the most expensive ones — they're the ones that show you paid attention.

Here are 10 personalised gift ideas guaranteed to make someone feel truly special:

**1. Engraved Leather Wallet Set**
A premium vegan leather wallet engraved with a name or initials is timeless. Pair it with a keychain and passport cover for a complete set that will be used every single day.

**2. Custom Star Map**
Capture the exact night sky from the date of a wedding, birth, or first meeting. These make incredible anniversary or birthday gifts.

**3. Name-Printed Tote Bag**
Eco-friendly, practical, and personal. A quality canvas tote with a name or meaningful quote is perfect for any age.

**4. Personalised Sling Bag**
Our faux leather sling bags can be customised with a name and charm — ideal for women who love a practical yet stylish accessory.

**5. Photo Memory Book**
Curate a handmade or printed photo album that tells the story of your relationship or their milestones.

**6. Custom Mug with Message**
Morning coffee tastes better in a mug that has their name or a private joke on it. Simple, affordable, and always appreciated.

**7. Embossed Diary & Pen Set**
For the creative soul, a leather-bound diary with their name and a matching pen is both thoughtful and functional.

**8. Personalised Corporate Hamper**
Celebrate your team with branded hampers featuring their names. It's a powerful way to show appreciation and build loyalty.

**9. Custom Keychain with Location Coordinates**
Engrave the GPS coordinates of a place that holds meaning — first home, favourite travel spot, or where they met.

**10. Name-Engraved Sunglass Holder**
Part of our executive gift set range, a personalised sunglass case adds a touch of luxury to everyday life.

The best gift is one that says "I thought of you specifically." Start personalising today!`,
    tags: ['personalised gifts', 'gift ideas', '2025', 'birthday', 'anniversary'],
  },
  {
    id: 2,
    slug: 'corporate-gifting-guide',
    title: 'The Complete Guide to Corporate Gifting That Builds Real Connections',
    category: 'Corporate',
    author: 'Feni Shah',
    date: 'March 28, 2025',
    readTime: '7 min read',
    excerpt: 'Corporate gifting is more than a gesture — it\'s a strategy. Learn how to choose gifts that strengthen client relationships and motivate your team.',
    cover: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
    body: `Corporate gifting has transformed from a seasonal obligation into a powerful relationship-building strategy. When done right, a well-chosen gift can leave a lasting impression that no email or meeting can replicate.

**Why Corporate Gifting Matters**

Studies show that 80% of recipients remember the brand that gave them a meaningful gift. It creates emotional touchpoints beyond transactional interactions — and in competitive markets, that goodwill is invaluable.

**Choosing the Right Gift**

The best corporate gifts share three qualities:
- **Useful** — they're used regularly, keeping your brand top-of-mind
- **Personal** — they acknowledge the individual, not just their job title
- **Quality** — they reflect the standard of your brand

**Our Top Corporate Gift Recommendations**

*Executive Leather Gift Sets*: Our 3-in-1 and 4-in-1 personalised sets (wallet, keychain, passport cover, sunglass holder) are bestsellers for good reason. Engrave each with the recipient's name for a truly personalised touch.

*Branded Hampers*: Premium dry fruit boxes, gourmet snack hampers, or wellness kits can be customised with your company branding and a personal note for each recipient.

*Custom Stationery Sets*: A leather-bound diary with branded pen and business card holder is practical, elegant, and daily-use.

**Budgeting for Corporate Gifts**

- Entry-level (₹300–₹600): Keychains, mugs, small leather goods
- Mid-range (₹600–₹1,500): Executive sets, hampers, custom apparel
- Premium (₹1,500+): Full leather gift sets, luxury hampers, personalised jewellery

**Ordering in Bulk**

For orders of 20 or more units, we offer custom pricing, brand-consistent packaging, and dedicated account management. Reach out to us on WhatsApp or via our Contact page for a tailored quote.

Start building real connections — one gift at a time.`,
    tags: ['corporate gifting', 'business', 'team appreciation', 'client gifts'],
  },
  {
    id: 3,
    slug: 'diwali-gifting-ideas',
    title: 'Diwali Gifting Ideas: Make This Festive Season Unforgettable',
    category: 'Festive',
    author: 'Feni Shah',
    date: 'October 5, 2024',
    readTime: '4 min read',
    excerpt: 'From premium dry fruit hampers to personalised diyas, discover the best Diwali gifts for family, friends, clients, and your whole team.',
    cover: 'https://images.unsplash.com/photo-1604548737895-038e22e0aad0?w=800&q=80',
    body: `Diwali — the festival of lights, sweets, and togetherness — is India's biggest gifting season. The right gift can brighten someone's celebration and leave an impression that lasts well beyond the festival.

**For Family & Friends**

*Personalised Dry Fruit Boxes*: A premium box of almonds, cashews, pistachios, and raisins, presented in a beautifully decorated wooden or acrylic box with a personalised name tag. Thoughtful and universally loved.

*Custom Diya & Candle Sets*: Hand-painted diyas paired with scented candles in festive packaging make for an elegant, traditional gift.

*Hampers with Sweets & Savouries*: Curate a hamper of artisanal mithais, chocolates, and gourmet snacks for a modern Diwali treat.

**For Corporate Clients & Teams**

Diwali is the perfect opportunity to strengthen business relationships. Our corporate Diwali hampers can be customised with your brand and each recipient's name. Popular choices include:
- Premium dry fruit boxes with branded packaging
- Engraved executive leather sets
- Wellness hampers with scented candles and skincare

**Tips for Diwali Gifting**

1. **Order Early** — Diwali is our busiest season. Place orders at least 2 weeks in advance to ensure timely delivery.
2. **Personalise** — Even a name tag or a handwritten message makes a mass-produced gift feel special.
3. **Go Eco-Friendly** — Choose recyclable packaging and sustainably sourced products where possible.
4. **Budget Smart** — Great Diwali gifts don't need to be expensive. A ₹500 personalised gift often means more than a generic ₹2,000 box.

Make this Diwali brighter for everyone you care about. Start your order today!`,
    tags: ['diwali', 'festive gifts', 'diwali hamper', 'indian gifts'],
  },
  {
    id: 4,
    slug: 'how-to-choose-gift-for-her',
    title: 'How to Choose the Perfect Gift for Her (Without the Guesswork)',
    category: 'Gift Guide',
    author: 'Feni Shah',
    date: 'February 8, 2025',
    readTime: '5 min read',
    excerpt: 'Stop settling for generic. Here\'s our complete guide to picking a gift she\'ll genuinely love — for birthdays, anniversaries, or just because.',
    cover: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=800&q=80',
    body: `Choosing a gift for the women in your life can feel daunting — but it doesn't have to be. The key is to think about *her*, not just "what women like."

**Step 1: Think About Her Daily Life**

The best gifts are the ones that seamlessly fit into someone's routine. Does she carry a handbag daily? A personalised sling bag or wallet set is perfect. Does she love mornings? A name-printed mug or coffee hamper hits differently.

**Step 2: Make It Personal**

Generic gifts get generic reactions. Adding her name, initials, or a meaningful date transforms any product into something she'll keep forever. Our personalised sling bags, for example, can be engraved with her name and a charm of her choice.

**Step 3: Match the Occasion**

- *Birthday*: Skincare hamper, personalised jewellery, custom photo book
- *Anniversary*: Rose gold jewellery set, couple memory book, spa hamper
- *Promotion/Achievement*: Elegant leather handbag, custom planner, executive set
- *Just Because*: A small personalised keychain, a scented candle, or a heartfelt note with a small gift

**Our Top Picks for Her**

1. Personalised Premium Sling Bag (available in Black, Blue, Tan, Olive, Maroon)
2. Customised jewellery with name pendant
3. Spa & wellness hamper
4. Name-printed leather wallet
5. Custom star map print

**The Golden Rule**

The thought you put in matters more than the price tag. A ₹450 personalised sling bag with her name means infinitely more than a ₹2,000 generic luxury gift. Choose with intention, and she'll know.`,
    tags: ['gifts for her', 'birthday', 'anniversary', 'personalised', 'women'],
  },
  {
    id: 5,
    slug: 'gifting-on-a-budget',
    title: 'Thoughtful Gifting on Any Budget: Our Best Picks Under ₹500',
    category: 'Budget Gifting',
    author: 'Feni Shah',
    date: 'January 15, 2025',
    readTime: '3 min read',
    excerpt: 'Great gifting doesn\'t require a big budget. Discover our favourite personalised gifts under ₹500 that feel anything but cheap.',
    cover: 'https://images.unsplash.com/photo-1607006344380-b6775a0824a7?w=800&q=80',
    body: `The most meaningful gifts aren't always the most expensive. Here are our favourite personalised picks that won't stretch your wallet but will absolutely make someone smile.

**Under ₹300**

- Custom keychain with name engraving
- Personalised name badge or luggage tag
- Photo fridge magnet
- Handwritten letter with a small treat

**₹300–₹450**

- Personalised sling bag (our bestseller — available in multiple colours!)
- Name-printed ceramic mug
- Custom bookmark set
- Mini personalised photo book

**₹450–₹700**

- Executive wallet with name engraving
- 3-in-1 leather gift set (wallet, keychain, sunglass holder)
- Personalised phone case
- Custom tote bag with message

**Pro Tips for Budget Gifting**

1. **Bundle small items** — A personalised keychain + a handwritten note + a small sweet makes for a beautiful set under ₹400.
2. **Focus on personalisation** — A simple gift with a name or message feels luxurious.
3. **Presentation matters** — Even a modest gift in beautiful wrapping feels elevated. All our orders include complimentary gift wrapping.
4. **Early ordering saves money** — Rush orders sometimes carry surcharges. Plan ahead and save.

Great gifting is about intention, not price. Let's find the perfect gift for your budget — browse our collection today!`,
    tags: ['budget gifts', 'affordable gifts', 'personalised', 'gifting tips'],
  },
  {
    id: 6,
    slug: 'care-for-leather-gifts',
    title: 'How to Care for Your Personalised Leather Gifts',
    category: 'Product Care',
    author: 'Feni Shah',
    date: 'December 20, 2024',
    readTime: '3 min read',
    excerpt: 'Learn how to keep your personalised leather wallets, bags, and accessories looking pristine for years to come.',
    cover: 'https://images.unsplash.com/photo-1589459072535-550a8e35ae72?w=800&q=80',
    body: `A well-maintained leather gift can last years — even decades. Here's how to keep your personalised leather accessories looking their very best.

**Daily Care**

- Avoid prolonged exposure to direct sunlight, which fades and dries leather.
- Keep away from water and moisture. If it gets wet, pat dry immediately with a soft cloth — never rub.
- Store in a cool, dry place when not in use.

**Cleaning**

- For surface dust, use a soft, dry cloth.
- For light stains, lightly dampen a cloth with distilled water and gently wipe. Allow to air dry.
- Avoid using alcohol, acetone, or harsh cleaners — these will damage the finish and any engraving.

**Conditioning**

Every 3–6 months, apply a small amount of leather conditioner (for genuine leather) or faux leather conditioner to keep the material supple and prevent cracking.

**Protecting Engravings**

The engraved area is durable but not indestructible. Avoid scratching with sharp objects. A clear leather sealant spray can help protect engravings over time.

**Storage Tips**

- Store wallets and bags in dust bags or pillowcases when not in use.
- Don't stack heavy items on top of leather goods — they can lose their shape.
- Stuff bags lightly with tissue paper to maintain form during storage.

With just a little care, your personalised leather gift will remain a cherished keepsake for years to come.`,
    tags: ['product care', 'leather', 'maintenance', 'gifting tips'],
  },
];

const categories = ['All', ...new Set(blogPosts.map((p) => p.category))];

/* ── Blog Post Detail ─────────────────────────────────── */
export const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) return (
    <div className="container" style={{ textAlign: 'center', padding: '80px 24px' }}>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', marginBottom: 12 }}>Post not found</h2>
      <Link to="/blogs" className="btn btn-primary-warm">Back to Blog</Link>
    </div>
  );

  const related = blogPosts.filter((p) => p.id !== post.id && p.category === post.category).slice(0, 3);

  const renderBody = (text) =>
    text.split('\n').map((line, i) => {
      if (!line.trim()) return <br key={i} />;
      if (line.startsWith('**') && line.endsWith('**'))
        return <h3 key={i} className="blog-body-h3">{line.slice(2, -2)}</h3>;
      if (line.startsWith('*') && line.endsWith('*'))
        return <p key={i} className="blog-body-italic">{line.slice(1, -1)}</p>;
      if (line.match(/^\d+\.\s/))
        return <p key={i} className="blog-body-list">{line}</p>;
      if (line.startsWith('- '))
        return <p key={i} className="blog-body-bullet">• {line.slice(2)}</p>;
      return <p key={i} className="blog-body-para">{line}</p>;
    });

  return (
    <div className="blog-detail-page">
      {/* Hero */}
      <div className="blog-detail-hero" style={{ backgroundImage: `url(${post.cover})` }}>
        <div className="blog-detail-hero__overlay" />
        <div className="container blog-detail-hero__content">
          <Link to="/blogs" className="blog-back-btn">← All Articles</Link>
          <span className="blog-cat-badge">{post.category}</span>
          <h1 className="blog-detail-title">{post.title}</h1>
          <div className="blog-detail-meta">
            <span>✍ {post.author}</span>
            <span>📅 {post.date}</span>
            <span>⏱ {post.readTime}</span>
          </div>
        </div>
      </div>

      <div className="container blog-detail-body">
        <div className="blog-detail-content">
          {renderBody(post.body)}
          <div className="blog-tags">
            {post.tags.map((tag) => (
              <span key={tag} className="blog-tag">#{tag}</span>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="blog-sidebar">
          <div className="blog-sidebar-card">
            <h3>About the Author</h3>
            <div className="sidebar-author">
              <img src="https://www.fcreation.in/web/image/1897-57a13554/1687279357254.jpeg" alt="Feni Shah"
                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&q=80'; }} />
              <div>
                <p className="sidebar-author__name">Feni Shah</p>
                <p className="sidebar-author__role">Founder, F Creation</p>
              </div>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7, marginTop: 12 }}>
              Creative soul behind F Creation Gifting Studio, passionate about meaningful design and personalised experiences.
            </p>
          </div>

          {related.length > 0 && (
            <div className="blog-sidebar-card">
              <h3>Related Articles</h3>
              {related.map((p) => (
                <Link key={p.id} to={`/blogs/${p.slug}`} className="sidebar-post">
                  <img src={p.cover} alt={p.title} />
                  <div>
                    <p className="sidebar-post__title">{p.title}</p>
                    <p className="sidebar-post__meta">{p.readTime}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="blog-sidebar-card blog-sidebar-card--cta">
            <span>🎁</span>
            <h3>Shop Our Collection</h3>
            <p>Find the perfect personalised gift for every occasion.</p>
            <Link to="/products" className="btn btn-primary-warm btn-sm" style={{ width: '100%', marginTop: 12 }}>Browse Gifts</Link>
          </div>
        </aside>
      </div>
    </div>
  );
};

/* ── Blog Listing ─────────────────────────────────────── */
const Blog = () => {
  const [activeCat, setActiveCat] = useState('All');
  const [search, setSearch] = useState('');

  const filtered = blogPosts.filter((p) =>
    (activeCat === 'All' || p.category === activeCat) &&
    (!search || p.title.toLowerCase().includes(search.toLowerCase()) || p.excerpt.toLowerCase().includes(search.toLowerCase()))
  );

  const featured = blogPosts[0];
  const rest     = filtered.filter((p) => p.id !== 1);

  return (
    <div className="blog-page">
      {/* Hero */}
      <section className="inner-hero">
        <div className="inner-hero__content container">
          <span className="about-eyebrow">Our Blog</span>
          <h1 className="inner-hero__title">Stories, Tips &<br />Gifting Inspiration</h1>
          <p className="inner-hero__sub">Ideas to help you give more meaningfully — for every person, every occasion.</p>
        </div>
      </section>

      <div className="container blog-body">
        {/* Filters */}
        <div className="blog-filters">
          <div className="blog-cat-pills">
            {categories.map((cat) => (
              <button
                key={cat}
                className={`faq-cat-pill ${activeCat === cat ? 'active' : ''}`}
                onClick={() => setActiveCat(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="blog-search-wrap">
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search articles…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="blog-search"
            />
          </div>
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <div className="faq-empty">
            <span>📝</span>
            <p>No articles found. <button className="btn btn-outline btn-sm" onClick={() => { setSearch(''); setActiveCat('All'); }}>Clear filters</button></p>
          </div>
        ) : (
          <div className="blog-grid">
            {(activeCat === 'All' && !search ? rest : filtered).map((post) => (
              <Link key={post.id} to={`/blogs/${post.slug}`} className="blog-card">
                <div className="blog-card__img-wrap">
                  <img src={post.cover} alt={post.title} className="blog-card__img" />
                  <span className="blog-card__cat">{post.category}</span>
                </div>
                <div className="blog-card__body">
                  <div className="blog-card__meta">
                    <span>{post.date}</span>
                    <span>·</span>
                    <span>{post.readTime}</span>
                  </div>
                  <h3 className="blog-card__title">{post.title}</h3>
                  <p className="blog-card__excerpt">{post.excerpt}</p>
                  <span className="blog-card__cta">Read more →</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
